import { ReactNode } from 'react';
import clsx from 'clsx';

export type TextButtonSize = 'medium' | 'small';
export type TextButtonVariant = 'brand' | 'neutral';
export type TextButtonFontWeight = 'normal' | 'bold';

export type TextButtonProps = {
  children: ReactNode;
  size?: TextButtonSize;
  variant?: TextButtonVariant;
  fontWeight?: TextButtonFontWeight;
  icon?: ReactNode;
  disabled?: boolean;
  className?: string;
  onClick?: () => void;
};

const sizeClasses: Record<TextButtonSize, string> = {
  medium: 'text-small py-1',
  small: 'text-x-small py-1',
};

const fontWeightClasses: Record<TextButtonFontWeight, string> = {
  normal: 'font-normal',
  bold: 'font-bold',
};

const variantClasses: Record<TextButtonVariant, string> = {
  brand: 'text-brand-highlight',
  neutral: 'text-neutral-primary',
};

export function TextButton({
  children,
  size = 'small',
  variant = 'brand',
  fontWeight = 'normal',
  icon,
  disabled = false,
  className,
  onClick,
  ...props
}: TextButtonProps) {
  const buttonClasses = clsx(
    'inline-flex items-center gap-2 transition-colors duration-200 ease-in-out',
    'focus:outline-none hover:opacity-40 active:opacity-70',
    sizeClasses[size],
    fontWeightClasses[fontWeight],
    disabled
      ? 'text-neutral-tertiary cursor-not-allowed pointer-events-none'
      : variantClasses[variant],
    className
  );

  const content = (
    <>
      {icon && <span className="flex-shrink-0 w-4 h-4">{icon}</span>}
      <span className="overflow-ellipsis overflow-hidden">{children}</span>
    </>
  );

  if (disabled) {
    return (
      <span className={buttonClasses} {...props} aria-disabled="true">
        {content}
      </span>
    );
  }

  return (
    <button
      className={buttonClasses}
      onClick={onClick}
      type="button"
      disabled={disabled}
      {...props}
    >
      {content}
    </button>
  );
}
